import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorText } from "@/lib/api";
import { SubjectBadge } from "@/components/SubjectBadge";

export const StudySheetForm = ({ open, onClose, sheet, terms, onSaved }) => {
  const [title, setTitle] = useState("");
  const [ids, setIds] = useState([]);
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setTitle(sheet?.title || ""); setIds(sheet?.term_ids || []); setFilter(""); }
  }, [open, sheet]);

  const toggle = (id) => setIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const shown = terms.filter((t) => !filter || `${t.word} ${t.subject}`.toLowerCase().includes(filter.toLowerCase()));

  const save = async () => {
    if (!title.trim() || !ids.length) return toast.error("Inserisci un titolo e scegli almeno una parola");
    setSaving(true);
    try {
      sheet ? await api.put(`/study-sheets/${sheet.id}`, { title, term_ids: ids }) : await api.post("/study-sheets", { title, term_ids: ids });
      toast.success("Foglio salvato");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl border-[2.5px] border-[var(--ink)] bg-white sm:max-w-2xl" data-testid="sheet-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{sheet ? "Modifica foglio" : "Nuovo foglio di studio"}</DialogTitle></DialogHeader>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder='es. "Verifica Storia 3A"' maxLength={80} data-testid="sheet-title-input" className="w-full rounded-xl border-2 border-[var(--ink)] px-3 py-2 font-semibold outline-none" />
        <div className="flex items-center gap-3">
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Cerca parola o materia…" data-testid="sheet-filter-input" className="flex-1 rounded-xl border-2 border-[var(--ink)] px-3 py-2 text-sm font-semibold outline-none" />
          <span className="text-sm font-extrabold" data-testid="sheet-selected-count">{ids.length} selezionate</span>
        </div>
        <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
          {shown.map((t) => (
            <label key={t.id} className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 border-[var(--ink)] px-3 py-2 ${ids.includes(t.id) ? "bg-[var(--sun)]/50" : "bg-white"}`}>
              <input type="checkbox" checked={ids.includes(t.id)} onChange={() => toggle(t.id)} data-testid={`sheet-term-checkbox-${t.id}`} className="h-4 w-4" />
              <span className="flex-1 font-bold">{t.word}</span>
              <SubjectBadge subject={t.subject} />
            </label>
          ))}
        </div>
        <button onClick={save} disabled={saving} data-testid="sheet-save-button" className="ink-box-sm ink-press flex items-center justify-center gap-2 rounded-full bg-[var(--sky)] py-3 font-extrabold text-white disabled:opacity-60">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salva foglio
        </button>
      </DialogContent>
    </Dialog>
  );
};
